import type { Feature, MultiPolygon, Polygon } from 'geojson';
import type { Coordinate } from 'ol/coordinate';
import turfArea from '@turf/area';
import booleanIntersects from '@turf/boolean-intersects';
import turfBuffer from '@turf/buffer';
import centerOfMass from '@turf/center-of-mass';
import difference from '@turf/difference';
import turfDistance from '@turf/distance';
import { featureCollection, point } from '@turf/helpers';
import intersect from '@turf/intersect';
import { SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT, SQ_M_PER_ACRE } from '../../constants';
import { Mission, Sample, SampleZone, SamplingTypes, SoilCore } from '../../db';
import { cleanSet } from '../../utils';
import { placeCoresEqualArea, computeEqualAreaPlacement, type EqualAreaPlacerOptions } from './EqualAreaCorePlacer';

/** Min distance (m) from a core to its zone's edge. */
export const ZONE_EDGE_INSET_METERS = 2;

/**
 * Default extra weight (fraction) for "single-sample" polygons — parts too small
 * to earn a full core on their own. Baked production value; the Technician panel
 * tunes it. 0 disables the bump. See {@link allocateCoresAcrossSegments}.
 */
export const ISOLATED_BOOST_DEFAULT = 0.25;

/**
 * Production default for {@link AutoCorePlacerOptions.skipSmallSegments}. The
 * orchestrator ({@link AutoCorePlacer}) injects this when the caller passes no
 * override, so the plain Auto-place button (`autoPlaceCores()`) and the Technician
 * panel agree: below-average segments compete for cores rather than being dropped.
 * The low-level {@link allocateCoresAcrossSegments} keeps its own utility default
 * of `true` — production policy lives here, alongside the area rails.
 */
export const SKIP_SMALL_SEGMENTS_DEFAULT = false;

/**
 * Absolute-area rails on segment allocation (square meters), independent of a
 * segment's *relative* size within its zone. Expressed in acres for agronomic
 * legibility. The orchestrator injects only the floor into
 * {@link allocateCoresAcrossSegments} at every call site, so it holds for every
 * production placement — an invariant, not a per-run knob. The guarantee is
 * retained (and unit-tested) but left at its disabled default in production.
 *
 * Floor: a segment below this is a sliver we never core — dropped unconditionally,
 * overriding `skipSmallSegments` and `isolatedBoost`. Guarantee: a segment at or
 * above this always earns at least one core no matter how small it is relative to
 * the per-core area target (`avgArea`); it steals a core from the largest survivor
 * if proportional rounding would otherwise leave it empty. Invariant: floor < guarantee.
 */
export const MIN_COREABLE_SEGMENT_AREA_SQM = 0.15 * SQ_M_PER_ACRE;
export const GUARANTEED_CORE_SEGMENT_AREA_SQM = 0.5 * SQ_M_PER_ACRE;

/** Orchestrator options: the kernel knobs plus segment-allocation behavior. */
export interface AutoCorePlacerOptions extends EqualAreaPlacerOptions {
  /**
   * Drop segments smaller than `total_area / cores`. When false — the production
   * default ({@link SKIP_SMALL_SEGMENTS_DEFAULT}) — small pieces compete for cores
   * proportionally like any other segment. Omitted → {@link SKIP_SMALL_SEGMENTS_DEFAULT}.
   */
  skipSmallSegments?: boolean;
  /**
   * Extra weight for single-sample polygons (parts below one core's worth of
   * area), so a standalone polygon is likelier to keep its one core. Defaults to
   * {@link ISOLATED_BOOST_DEFAULT}; 0 disables.
   */
  isolatedBoost?: number;
}

/**
 * Allocate `totalCores` across N polygon segments, weighted by area.
 *
 * With `skipSmallSegments` (default): segments smaller than
 * `total_area / totalCores` are dropped (return 0). Survivors get
 * `floor(totalCores * area_i / sum(surviving_areas))`, with the remainder
 * distributed to segments with the largest fractional parts (largest-remainder
 * method). Each surviving segment is guaranteed at least 1.
 *
 * With `skipSmallSegments = false`: no segment is dropped; every part competes
 * via largest-remainder — small pieces can win a core on their fraction, but
 * there is NO min-1 guarantee (a part can still round to 0).
 *
 * `isolatedBoost` favors "single-sample" polygons: any part below one core's
 * worth of area (`avgArea = total / cores`) has its allocation weight scaled by
 * `1 + isolatedBoost`, making a standalone polygon likelier to clear the skip
 * threshold / win a remainder core. Larger (multi-sample) parts are never
 * bumped, so the spare core is drawn from them; a part still under-area after
 * the bump stays at 0 (no guarantee). Defaults to {@link ISOLATED_BOOST_DEFAULT}.
 *
 * Two absolute-area rails sit on top of the relative logic, independent of how a
 * segment compares to `avgArea` (both default to disabled — production passes only
 * {@link MIN_COREABLE_SEGMENT_AREA_SQM}; the guarantee is left at its disabled default):
 *  • `minCoreSegmentAreaSqm` — a segment below this is never cored (dropped
 *    unconditionally, overriding both `skipSmallSegments` and `isolatedBoost`).
 *  • `guaranteedCoreSegmentAreaSqm` — a segment at/above this always earns ≥ 1
 *    core regardless of the skip flag or its fraction, stealing from the largest
 *    survivor if rounding left it empty.
 *
 * Pigeonhole: at least one segment is ≥ avg, so the largest always survives
 * (unless every segment is below the floor, in which case none are cored).
 */
export function allocateCoresAcrossSegments(
  segmentAreas: number[],
  totalCores: number,
  skipSmallSegments = true,
  isolatedBoost = ISOLATED_BOOST_DEFAULT,
  minCoreSegmentAreaSqm = 0,
  guaranteedCoreSegmentAreaSqm = Number.POSITIVE_INFINITY,
): number[] {
  if (segmentAreas.length === 0 || totalCores <= 0) {
    return segmentAreas.map(() => 0);
  }

  const totalArea = segmentAreas.reduce((a, b) => a + b, 0);
  const avgArea = totalArea / totalCores;

  // Single-sample polygons (below one core's worth of area) get a weight bump so
  // a standalone piece is likelier to clear the skip threshold and hold its one
  // core. Multi-sample parts (≥ avgArea) are never bumped → the spare core comes
  // from them. A part still under-area after the bump stays skipped.
  const weights = segmentAreas.map((a) => (a < avgArea ? a * (1 + isolatedBoost) : a));

  // Absolute-area rails (square meters), independent of relative size:
  //  • floor — a sub-floor sliver is never cored, overriding skip / isolatedBoost.
  //  • guarantee — a part at/above it always earns ≥ 1 core (enforced after the
  //    proportional split below), even when tiny relative to avgArea.
  const aboveFloor = segmentAreas.map((a) => a >= minCoreSegmentAreaSqm);
  const guaranteed = segmentAreas.map((a) => a >= guaranteedCoreSegmentAreaSqm);

  const alive = segmentAreas.map(
    (_, i) => aboveFloor[i] && (!skipSmallSegments || weights[i] >= avgArea || guaranteed[i]),
  );
  const survivingTotal = weights.reduce((acc, w, i) => acc + (alive[i] ? w : 0), 0);

  if (survivingTotal === 0) {
    // Shouldn't happen by pigeonhole, but defensive.
    return segmentAreas.map(() => 0);
  }

  const raws = weights.map((w, i) => (alive[i] ? (totalCores * w) / survivingTotal : 0));
  const floors = raws.map((r) => Math.floor(r));
  let remainder = totalCores - floors.reduce((a, b) => a + b, 0);

  // Distribute remainder to largest fractional parts among survivors.
  const fracs = raws.map((r, i) => ({ i, frac: r - Math.floor(r) }));
  fracs.sort((a, b) => b.frac - a.frac);

  const result = [...floors];

  for (const { i } of fracs) {
    if (remainder <= 0) {
      break;
    }

    if (!alive[i]) {
      continue;
    }

    result[i] += 1;
    remainder -= 1;
  }

  // Min 1 per segment that must hold a core. Two triggers:
  //  • skipSmallSegments on → every survivor (its raw quota is already ≥ 1, so
  //    this is a defensive net). With the filter OFF the allocation is purely
  //    proportional: a sub-quota sliver legitimately rounds to 0 and must NOT
  //    steal a core — so survivors alone do not qualify.
  //  • guaranteed (≥ guaranteedCoreSegmentAreaSqm) → always, regardless of the
  //    skip flag, so a large standalone part is never left empty by rounding.
  for (let i = 0; i < result.length; i++) {
    const mustHaveCore = alive[i] && (skipSmallSegments || guaranteed[i]);

    if (!mustHaveCore) {
      continue;
    }

    if (result[i] > 0) {
      continue;
    }

    // Find largest allocation and steal one.
    let maxIdx = -1;

    for (let j = 0; j < result.length; j++) {
      if (j === i) {
        continue;
      }

      if (result[j] > 1 && (maxIdx === -1 || result[j] > result[maxIdx])) {
        maxIdx = j;
      }
    }

    if (maxIdx === -1) {
      break;
    } // can't steal — only one survivor, edge case

    result[maxIdx] -= 1;
    result[i] += 1;
  }

  return result;
}

/**
 * Order a list of points by greedy nearest-neighbor from the start point.
 *
 * Distance is Euclidean in the input space — caller is responsible for using a
 * locally-flat projection (e.g., short distances in lon/lat at mid-latitudes
 * are fine; for cross-hemisphere distances use geodesic distance instead).
 */
export function greedyNearestNeighborOrder(points: Coordinate[], start: Coordinate): Coordinate[] {
  const remaining = points.slice();
  const ordered: Coordinate[] = [];
  let current = start;

  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const [x, y] = remaining[i];
      const dx = x - current[0];
      const dy = y - current[1];
      const d2 = dx * dx + dy * dy;

      if (d2 < bestDist) {
        bestDist = d2;
        bestIdx = i;
      }
    }

    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    current = next;
  }

  return ordered;
}

/**
 * Inset a sample-zone segment so any point inside the result is safe for coring:
 *   - ≥ SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT (5m) from the field edge
 *   - ≥ ZONE_EDGE_INSET_METERS (2m) from the zone's own edge
 *   - outside every unsafe zone
 *
 * Returns null if no safe area remains.
 */
export function insetSegmentForCoring(
  segment: Feature<Polygon>,
  fieldBoundary: Feature<Polygon | MultiPolygon>,
  unsafeZones: Feature<Polygon | MultiPolygon>[],
): Feature<Polygon | MultiPolygon> | null {
  const safeField = turfBuffer(fieldBoundary, -SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT, {
    units: 'meters',
  });

  if (!safeField) {
    return null;
  }

  const insetSegment = turfBuffer(segment, -ZONE_EDGE_INSET_METERS, { units: 'meters' });

  if (!insetSegment) {
    return null;
  }

  // Intersect: insetSegment ∩ safeField
  let result: Feature<Polygon | MultiPolygon> | null = intersect(featureCollection([insetSegment, safeField]));

  if (!result) {
    return null;
  }

  // Subtract each unsafe zone.
  for (const unsafe of unsafeZones) {
    if (!result) {
      break;
    }

    // turf v7 difference returns null both when the result would be empty
    // AND when the inputs don't overlap. Disambiguate with booleanIntersects.
    const overlaps = booleanIntersects(result, unsafe);

    if (!overlaps) {
      continue;
    }

    result = difference(featureCollection([result, unsafe]));
  }

  return result;
}

// ============================================================================
// AutoCorePlacer — mission-aware orchestrator
// ============================================================================

export interface AutoCorePlacementResult {
  placed: number;
  skippedZones: string[];
  skippedSegments: number;
}

/** One sample-zone segment's rasterized grid, for the Technician grid overlay. */
export interface CoreGridSegment {
  /** Grid-sample centers (lon/lat). */
  samples: Coordinate[];
  /** Cell index per sample (0..k-1 within this segment), parallel to `samples`. */
  assign: number[];
  /** Grid spacing in meters (square size for display). */
  stepMeters: number;
  /** Color-seed offset so cells across segments get distinct colors. */
  colorSeedBase: number;
}

/**
 * Orchestrates auto-placement of `spec.cores` SoilCores into every SAMPLE_ZONE
 * of a zone or zone-interleave mission.
 *
 * Algorithm:
 *  1. Validate (spec.cores > 0; no pulled cores).
 *  2. Order sample zones by greedy nearest-neighbor from `getStartingPosition()`.
 *  3. For each zone (in order):
 *     a. Split its multipolygon into segment polygons.
 *     b. Inset each segment: subtract unsafe zones, inset 5m from field, 1m from zone edge.
 *     c. Flatten any MultiPolygon results into individual sub-segments.
 *     d. Allocate cores across sub-segments by area (largest-remainder; drops too-small).
 *     e. Run Lloyd's relaxation per sub-segment to get the allocated point count.
 *     f. Order all in-zone coords by greedy NN from the previous zone's exit point.
 *     g. Write a SoilCore for each coord. Set `sample.order` to the zone's position.
 *
 * Does not call `calculate_path()` — that's the caller's job (the UI button).
 * Throws on invalid input; the caller is responsible for confirm-then-wipe before
 * invoking.
 */
export class AutoCorePlacer {
  constructor(
    private mission: Mission,
    private placerOptions?: AutoCorePlacerOptions,
  ) {}

  async placeAll(): Promise<AutoCorePlacementResult> {
    const spec = this.mission.getPrimarySpec();

    if (!spec || spec.cores <= 0) {
      throw new Error(`Auto-place requires spec.cores > 0 (got ${spec?.cores})`);
    }

    const sampleZones = cleanSet(this.mission.getZones().getSampleZones());

    if (sampleZones.length === 0) {
      return { placed: 0, skippedZones: [], skippedSegments: 0 };
    }

    const samplesWithPulled = sampleZones.some((sz) => {
      const samples = sz.getSampleSite()?.getSamples() ?? [];

      return cleanSet(samples).some((sample) => cleanSet(sample.getSoilCores()).some((c) => c.pulled));
    });

    if (samplesWithPulled) {
      throw new Error(
        'Auto-place refuses to run: one or more zones have pulled cores. Auto-place is for unsampled missions only.',
      );
    }

    const boundaryZone = this.mission.getBoundary();

    if (!boundaryZone) {
      throw new Error('Auto-place requires a field boundary, none found');
    }

    const fieldBoundary = boundaryZone.toMultiPolygon();

    if (fieldBoundary.geometry.coordinates.length === 0) {
      throw new Error('Auto-place requires a field boundary, found empty boundary');
    }

    const unsafePolygons = this.collectUnsafePolygons();
    const orderedZones = this.greedyOrderSampleZones(sampleZones);

    const skippedZones: string[] = [];
    let skippedSegments = 0;
    let placed = 0;
    let exitPoint = this.startCoord();

    for (let zoneIdx = 0; zoneIdx < orderedZones.length; zoneIdx++) {
      const sampleZone = orderedZones[zoneIdx];
      const zone = sampleZone.getZone();
      const sampleSite = sampleZone.getSampleSite();

      if (!zone || !sampleSite) {
        continue;
      }

      const sample = this.getOrCreateSample(sampleZone, spec);
      sample.order = zoneIdx + 1;

      const insetSegments = this.buildInsetSegments(zone, fieldBoundary, unsafePolygons);

      // Segments dropped because their inset became empty (too small after offsets).
      skippedSegments += insetSegments.skipped;

      if (insetSegments.segments.length === 0) {
        skippedZones.push(zone.zone_name);
        continue;
      }

      const segmentAreas = insetSegments.segments.map((s) => turfArea(s));
      const allocation = allocateCoresAcrossSegments(
        segmentAreas,
        spec.cores,
        this.placerOptions?.skipSmallSegments ?? SKIP_SMALL_SEGMENTS_DEFAULT,
        this.placerOptions?.isolatedBoost,
        MIN_COREABLE_SEGMENT_AREA_SQM,
      );

      // Segments dropped because they fell below `total_area / N` (the area-allocation rule).
      skippedSegments += allocation.filter((n) => n === 0).length;

      const allCoords: Coordinate[] = [];

      for (let i = 0; i < insetSegments.segments.length; i++) {
        const k = allocation[i];

        if (k === 0) {
          continue;
        }

        const seg = insetSegments.segments[i];
        const seeds = placeCoresEqualArea(seg, k, this.placerOptions);
        allCoords.push(...seeds);
      }

      const orderedCoords = greedyNearestNeighborOrder(allCoords, exitPoint);

      for (const [lon, lat] of orderedCoords) {
        const core = SoilCore.create();
        core.Sample_id = sample.instance_id;
        core.core_number = Math.max(...cleanSet(sample.getSoilCores()).map((c) => c.core_number)) + 1;
        core.lat = lat;
        core.lon = lon;
        core.source = 'Rogo Map Maker';
      }

      placed += orderedCoords.length;

      if (orderedCoords.length > 0) {
        exitPoint = orderedCoords[orderedCoords.length - 1];
      }
    }

    return { placed, skippedZones, skippedSegments };
  }

  /**
   * Compute the rasterized grid (samples + per-segment cell assignment) the
   * kernel would use for every sample zone, WITHOUT placing any cores — for the
   * Technician grid overlay. Uses the same inset + allocation + options as
   * {@link placeAll}. Lenient: returns `[]` if the mission isn't ready.
   */
  computeGrids(): CoreGridSegment[] {
    const spec = this.mission.getPrimarySpec();

    if (!spec || spec.cores <= 0) {
      return [];
    }

    const sampleZones = cleanSet(this.mission.getZones().getSampleZones());

    if (sampleZones.length === 0) {
      return [];
    }

    const boundaryZone = this.mission.getBoundary();

    if (!boundaryZone) {
      return [];
    }

    const fieldBoundary = boundaryZone.toMultiPolygon();

    if (fieldBoundary.geometry.coordinates.length === 0) {
      return [];
    }

    const unsafePolygons = this.collectUnsafePolygons();
    const grids: CoreGridSegment[] = [];
    let colorSeedBase = 0;

    for (const sampleZone of sampleZones) {
      const zone = sampleZone.getZone();

      if (!zone) {
        continue;
      }

      const insetSegments = this.buildInsetSegments(zone, fieldBoundary, unsafePolygons);

      if (insetSegments.segments.length === 0) {
        continue;
      }

      const segmentAreas = insetSegments.segments.map((s) => turfArea(s));
      const allocation = allocateCoresAcrossSegments(
        segmentAreas,
        spec.cores,
        this.placerOptions?.skipSmallSegments ?? SKIP_SMALL_SEGMENTS_DEFAULT,
        this.placerOptions?.isolatedBoost,
        MIN_COREABLE_SEGMENT_AREA_SQM,
      );

      for (let i = 0; i < insetSegments.segments.length; i++) {
        const k = allocation[i];

        if (k === 0) {
          continue;
        }

        const placement = computeEqualAreaPlacement(insetSegments.segments[i], k, this.placerOptions);

        if (placement.gridSamples.length === 0) {
          continue;
        }

        grids.push({
          samples: placement.gridSamples,
          assign: placement.gridAssign,
          stepMeters: placement.stepMeters,
          colorSeedBase,
        });
        colorSeedBase += k; // reserve k color slots so the next segment's cells differ
      }
    }

    return grids;
  }

  private startCoord(): Coordinate {
    const { lat, lon } = this.mission.getStartingPosition();

    return [lon, lat];
  }

  private collectUnsafePolygons(): Feature<MultiPolygon>[] {
    return cleanSet(this.mission.getUnsafeZones())
      .map((z) => z.toMultiPolygon())
      .filter((mp) => mp.geometry.coordinates.length > 0);
  }

  /** Greedy nearest-zone ordering from the mission's starting position. */
  private greedyOrderSampleZones(sampleZones: SampleZone[]): SampleZone[] {
    type WithCentroid = { sampleZone: SampleZone; centroid: Coordinate };

    const withCentroids: WithCentroid[] = sampleZones
      .map((sz) => {
        const zone = sz.getZone();

        if (!zone) {
          return null;
        }

        const mp = zone.toMultiPolygon();

        if (mp.geometry.coordinates.length === 0) {
          return null;
        }

        const com = centerOfMass(mp).geometry.coordinates as Coordinate;

        return { sampleZone: sz, centroid: com };
      })
      .filter((x): x is WithCentroid => x !== null);

    const remaining = withCentroids.slice();
    const ordered: SampleZone[] = [];
    let current = this.startCoord();

    while (remaining.length > 0) {
      let bestIdx = 0;
      let bestDist = Infinity;

      for (let i = 0; i < remaining.length; i++) {
        const d = turfDistance(point(current), point(remaining[i].centroid), {
          units: 'meters',
        });

        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }

      const next = remaining.splice(bestIdx, 1)[0];
      ordered.push(next.sampleZone);
      current = next.centroid;
    }

    return ordered;
  }

  /** Inset every segment in `zone`; flatten any MultiPolygon results into individual polygons. */
  private buildInsetSegments(
    zone: ReturnType<SampleZone['getZone']> & object,
    fieldBoundary: Feature<Polygon | MultiPolygon>,
    unsafePolygons: Feature<Polygon | MultiPolygon>[],
  ): { segments: Feature<Polygon>[]; skipped: number } {
    const mp = zone.toMultiPolygon();
    const segments: Feature<Polygon>[] = [];
    let skipped = 0;

    for (const segCoords of mp.geometry.coordinates) {
      const segment: Feature<Polygon> = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: segCoords },
      };
      const inset = insetSegmentForCoring(segment, fieldBoundary, unsafePolygons);

      if (!inset) {
        skipped++;
        continue;
      }

      if (inset.geometry.type === 'Polygon') {
        segments.push(inset as Feature<Polygon>);
        continue;
      }

      // MultiPolygon — flatten.
      const polyCoords = (inset.geometry as MultiPolygon).coordinates;

      for (const polyCoord of polyCoords) {
        segments.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: polyCoord },
        });
      }
    }

    return { segments, skipped };
  }

  private getOrCreateSample(sampleZone: SampleZone, spec: NonNullable<ReturnType<Mission['getPrimarySpec']>>): Sample {
    const sampleSite = sampleZone.getSampleSite();

    if (!sampleSite) {
      throw new Error(`Sample zone ${sampleZone.instance_id} has no SampleSite`);
    }

    const existing = cleanSet(sampleSite.getSamples())[0];

    if (existing) {
      return existing;
    }

    return Sample.createSample(this.mission, sampleSite, spec, sampleSite.original_sample_id, SamplingTypes.REGULAR);
  }
}
